import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useInView } from "react-intersection-observer";
import {
  Zap,
  Search,
  Target,
  Network,
  PenTool,
  Rocket,
  Smartphone,
  Building,
  Cloud,
  RefreshCw,
  Users,
  Cpu,
  Brain,
  Eye,
  BarChart,
  Shield,
  Database,
  Wifi,
  Settings,
  Lock,
  ChevronRight,
  Sparkles,
  CheckCircle,
  Globe,
  Code,
  Server,
  Terminal,
  ShieldCheck,
  Layers,
  BarChart3,
  Cpu as CpuIcon,
  TrendingUp,
  Smartphone as PhoneIcon,
} from "lucide-react";

const servicesData = {
  growth: {
    title: "GROWTH ENGINE",
    subtitle: "Accelerate your market presence with data-driven strategies",
    color: "cyan",
    icon: Zap,
    modules: [
      {
        id: 1,
        title: "Search Dominance",
        description: "Advanced SEO strategies to dominate search rankings and drive organic traffic",
        capabilities: ["Enterprise SEO", "Local SEO", "Technical SEO", "Content Strategy"],
        icon: Search,
        stats: "98% Success Rate"
      },
      {
        id: 2,
        title: "Performance Marketing",
        description: "Data-driven paid campaigns for maximum ROI across all channels",
        capabilities: ["Google Ads", "Social Media", "Display Networks", "Retargeting"],
        icon: Target,
        stats: "300% Average ROAS"
      },
      {
        id: 3,
        title: "Social Strategy",
        description: "Build brand authority and engagement through strategic social presence",
        capabilities: ["Content Creation", "Community Management", "Influencer Marketing", "Analytics"],
        icon: Network,
        stats: "85% Engagement Increase"
      },
      {
        id: 4,
        title: "Brand Storytelling",
        description: "Craft compelling narratives that connect with your target audience",
        capabilities: ["Copywriting", "Video Production", "Email Marketing", "Content Strategy"],
        icon: PenTool,
        stats: "2x Conversion Rates"
      },
      {
        id: 5,
        title: "E-commerce Growth",
        description: "Optimize online stores for maximum conversions and customer retention",
        capabilities: ["Store Optimization", "Conversion Rate", "Customer Journey", "Analytics"],
        icon: Rocket,
        stats: "40% Revenue Growth"
      }
    ]
  },
  build: {
    title: "TECHNOLOGY",
    subtitle: "Build robust digital products that scale with your business",
    color: "blue",
    icon: CpuIcon,
    modules: [
      {
        id: 1,
        title: "Mobile Apps",
        description: "Native and cross-platform mobile applications for iOS and Android",
        capabilities: ["iOS Development", "Android Development", "React Native", "Flutter"],
        icon: Smartphone,
        stats: "4.8 App Store Rating"
      },
      {
        id: 2,
        title: "Enterprise Software",
        description: "Custom solutions for large-scale business operations and management",
        capabilities: ["ERP Systems", "CRM Solutions", "Internal Tools", "Dashboard Design"],
        icon: Building,
        stats: "99.9% Uptime"
      },
      {
        id: 3,
        title: "SaaS Platforms",
        description: "Scalable software-as-a-service products with subscription management",
        capabilities: ["Multi-tenancy", "API Development", "Cloud Integration", "Subscription Models"],
        icon: Cloud,
        stats: "10K+ Daily Users"
      },
      {
        id: 4,
        title: "System Modernization",
        description: "Upgrade legacy systems to modern, efficient architectures",
        capabilities: ["Code Refactoring", "Migration", "Performance", "Security"],
        icon: RefreshCw,
        stats: "60% Performance Gain"
      },
      {
        id: 5,
        title: "Dedicated Teams",
        description: "Expert development teams integrated with your workflow",
        capabilities: ["Full Stack", "Frontend", "Backend", "DevOps"],
        icon: Users,
        stats: "100% Satisfaction"
      }
    ]
  },
  intelligence: {
    title: "AI SOLUTIONS",
    subtitle: "Leverage artificial intelligence for competitive advantage",
    color: "purple",
    icon: Brain,
    modules: [
      {
        id: 1,
        title: "Generative AI",
        description: "Custom AI models for content, design, and automation",
        capabilities: ["Chatbots", "Content Generation", "Image Creation", "Code Assistance"],
        icon: Brain,
        stats: "90% Time Saved"
      },
      {
        id: 2,
        title: "Machine Learning",
        description: "Predictive analytics and intelligent decision-making systems",
        capabilities: ["Predictive Models", "Recommendation Engines", "Pattern Recognition", "Data Mining"],
        icon: Cpu,
        stats: "95% Accuracy"
      },
      {
        id: 3,
        title: "Process Automation",
        description: "Automate repetitive tasks with intelligent workflows",
        capabilities: ["RPA", "Workflow Automation", "Task Scheduling", "Integration"],
        icon: Settings,
        stats: "80% Cost Reduction"
      },
      {
        id: 4,
        title: "Computer Vision",
        description: "Image and video analysis for various applications",
        capabilities: ["Object Detection", "Facial Recognition", "Quality Control", "Video Analytics"],
        icon: Eye,
        stats: "99% Detection Rate"
      },
      {
        id: 5,
        title: "Business Intelligence",
        description: "Transform data into actionable insights and reports",
        capabilities: ["Data Visualization", "Dashboard Creation", "Analytics", "Reporting"],
        icon: BarChart,
        stats: "50% Better Decisions"
      }
    ]
  },
  infrastructure: {
    title: "INFRASTRUCTURE",
    subtitle: "Secure and scalable technology foundations",
    color: "green",
    icon: ShieldCheck,
    modules: [
      {
        id: 1,
        title: "Cloud Services",
        description: "Complete cloud solutions for hosting and scaling applications",
        capabilities: ["AWS", "Azure", "Google Cloud", "Serverless"],
        icon: Cloud,
        stats: "99.9% Availability"
      },
      {
        id: 2,
        title: "Blockchain",
        description: "Decentralized applications and smart contract development",
        capabilities: ["Smart Contracts", "NFT Platforms", "DeFi", "Wallet Integration"],
        icon: Database,
        stats: "100% Security"
      },
      {
        id: 3,
        title: "IoT Solutions",
        description: "Connect physical devices with digital systems",
        capabilities: ["Device Integration", "Real-time Data", "Remote Control", "Analytics"],
        icon: Wifi,
        stats: "10K+ Devices"
      },
      {
        id: 4,
        title: "DevOps",
        description: "Automated development pipelines and infrastructure management",
        capabilities: ["CI/CD", "Containerization", "Monitoring", "Deployment"],
        icon: Settings,
        stats: "80% Faster Deployments"
      },
      {
        id: 5,
        title: "Cybersecurity",
        description: "Protect your digital assets with advanced security measures",
        capabilities: ["Security Audits", "Penetration Testing", "Encryption", "Compliance"],
        icon: Lock,
        stats: "Zero Breaches"
      }
    ]
  }
};

const Slide4Services = () => {
  const [activeService, setActiveService] = useState("growth");
  const [hoveredModule, setHoveredModule] = useState(null);
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: false
  });

  const serviceTypes = [
    { id: "growth", name: "Growth", icon: Zap, color: "cyan" },
    { id: "build", name: "Technology", icon: CpuIcon, color: "blue" },
    { id: "intelligence", name: "AI", icon: Brain, color: "purple" },
    { id: "infrastructure", name: "Infrastructure", icon: ShieldCheck, color: "green" },
  ];

  const currentService = servicesData[activeService];
  const ServiceIcon = currentService.icon;

  const colorVariants = {
    cyan: {
      bg: "bg-gradient-to-br from-cyan-500/10 to-cyan-500/5",
      text: "text-cyan-400",
      border: "border-cyan-400/30",
      hover: "hover:border-cyan-400/50",
      button: "bg-gradient-to-r from-cyan-500 to-cyan-600"
    },
    blue: {
      bg: "bg-gradient-to-br from-blue-500/10 to-blue-500/5",
      text: "text-blue-400",
      border: "border-blue-400/30",
      hover: "hover:border-blue-400/50",
      button: "bg-gradient-to-r from-blue-500 to-blue-600"
    },
    purple: {
      bg: "bg-gradient-to-br from-purple-500/10 to-purple-500/5",
      text: "text-purple-400",
      border: "border-purple-400/30",
      hover: "hover:border-purple-400/50",
      button: "bg-gradient-to-r from-purple-500 to-purple-600"
    },
    green: {
      bg: "bg-gradient-to-br from-green-500/10 to-green-500/5",
      text: "text-green-400",
      border: "border-green-400/30",
      hover: "hover:border-green-400/50",
      button: "bg-gradient-to-r from-green-500 to-green-600"
    }
  };

  const colors = colorVariants[currentService.color];

  return (
    <div 
      id="services" 
      ref={ref}
      className="relative min-h-screen w-full bg-black overflow-hidden py-8 md:py-12"
    >
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className={`absolute inset-0 ${colors.bg}`}></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black via-transparent to-black"></div>
        
        {/* Grid Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div 
            className="absolute inset-0" 
            style={{
              backgroundImage: `linear-gradient(90deg, currentColor 1px, transparent 1px),
                               linear-gradient(180deg, currentColor 1px, transparent 1px)`,
              backgroundSize: '40px 40px',
            }}
          ></div>
        </div>
      </div>

      <div className="relative z-10 container mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="text-center mb-8 md:mb-12 px-2">
          <div className="inline-flex items-center gap-2 mb-4 px-3 py-1.5 rounded-full bg-white/5 border border-white/10">
            <Sparkles className="w-3 h-3 text-cyan-400" />
            <span className="text-cyan-300 text-xs font-medium">OUR SERVICES</span>
          </div>
          
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-3 md:mb-4">
            <span className="block text-white mb-1">Comprehensive</span>
            <span className={`block ${colors.text} bg-clip-text text-transparent bg-gradient-to-r ${colors.text.replace('text-', 'from-')} ${currentService.color === 'cyan' ? 'to-blue-400' : currentService.color === 'blue' ? 'to-cyan-400' : currentService.color === 'purple' ? 'to-pink-400' : 'to-emerald-400'}`}>
              Digital Solutions
            </span>
          </h2>
          
          <p className="text-sm sm:text-base md:text-lg text-gray-400 max-w-2xl mx-auto">
            End-to-end services to build, grow, and scale your digital presence
          </p>
        </div>

        {/* Service Type Selector - Mobile First */}
        <div className="mb-6 md:mb-8">
          <div className="flex flex-wrap justify-center gap-2 md:gap-3">
            {serviceTypes.map((service) => {
              const Icon = service.icon;
              const isActive = activeService === service.id;
              return (
                <button
                  key={service.id}
                  onClick={() => setActiveService(service.id)}
                  className={`flex items-center gap-2 px-3 md:px-4 py-2 md:py-2.5 rounded-lg transition-all duration-300 ${
                    isActive
                      ? `${colorVariants[service.color].button} text-white shadow-lg`
                      : 'bg-white/5 text-gray-300 hover:bg-white/10'
                  }`}
                >
                  <Icon className="w-4 h-4 md:w-5 md:h-5" />
                  <span className="text-sm font-medium">{service.name}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Active Service Header */}
        <motion.div
          key={activeService}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="text-center mb-6 md:mb-8"
        >
          <div className="flex flex-col md:flex-row items-center justify-center gap-3 md:gap-4 mb-4">
            <div className={`p-2 md:p-3 rounded-xl ${colors.bg} border ${colors.border}`}>
              <ServiceIcon className={`w-6 h-6 md:w-8 md:h-8 ${colors.text}`} />
            </div>
            <div>
              <h3 className="text-xl md:text-2xl lg:text-3xl font-bold text-white">
                {currentService.title}
              </h3>
              <p className="text-sm md:text-base text-gray-300 mt-1">
                {currentService.subtitle}
              </p>
            </div>
          </div>
        </motion.div>

        {/* Service Modules Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mb-8 md:mb-12">
          {currentService.modules.map((module, index) => {
            const ModuleIcon = module.icon;
            return (
              <motion.div
                key={module.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                onMouseEnter={() => setHoveredModule(module.id)}
                onMouseLeave={() => setHoveredModule(null)}
                className={`group relative bg-gradient-to-br from-gray-900/80 to-black/80 rounded-xl md:rounded-2xl border ${colors.border} p-4 md:p-6 transition-all duration-300 ${colors.hover}`}
              >
                {/* Module Header */}
                <div className="flex items-start justify-between mb-3 md:mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${colors.bg}`}>
                      <ModuleIcon className={`w-5 h-5 md:w-6 md:h-6 ${colors.text}`} />
                    </div>
                    <div>
                      <h4 className="text-base md:text-lg font-bold text-white mb-1">
                        {module.title}
                      </h4>
                      <div className={`text-xs ${colors.text} font-medium`}>
                        {module.stats}
                      </div>
                    </div>
                  </div>
                  <div className="text-xs text-gray-500 font-mono">
                    0{module.id}
                  </div>
                </div>

                {/* Description */}
                <p className="text-sm md:text-base text-gray-300 mb-4 md:mb-6 leading-relaxed">
                  {module.description}
                </p>

                {/* Capabilities */}
                <div>
                  <div className={`text-xs ${colors.text} font-medium mb-3 flex items-center gap-2`}>
                    <div className="w-2 h-0.5 bg-current"></div>
                    Capabilities
                  </div>
                  <div className="space-y-2">
                    {module.capabilities.map((capability, capIndex) => (
                      <div
                        key={capIndex}
                        className="flex items-center gap-2 text-sm text-gray-400"
                      >
                        <CheckCircle className="w-3 h-3 md:w-4 md:h-4 text-green-400" />
                        <span>{capability}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Hover Effect */}
                <div className={`absolute inset-0 rounded-xl md:rounded-2xl border-2 ${colors.border} opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none`}></div>
              </motion.div>
            );
          })}
        </div>

        {/* Service Highlights - Mobile Responsive */}
        <div className="mb-8 md:mb-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {[
              { label: "Projects", value: "200+", icon: Layers, color: colors.text },
              { label: "Success Rate", value: "98%", icon: BarChart3, color: "text-green-400" },
              { label: "Client Growth", value: "300%", icon: TrendingUp, color: colors.text },
              { label: "Satisfaction", value: "100%", icon: Users, color: "text-yellow-400" },
            ].map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div
                  key={index}
                  className="bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-lg p-3 md:p-4 text-center"
                >
                  <div className="flex justify-center mb-2">
                    <Icon className={`w-4 h-4 md:w-5 md:h-5 ${stat.color}`} />
                  </div>
                  <div className="text-lg md:text-xl font-bold text-white mb-1">
                    {stat.value}
                  </div>
                  <div className="text-xs text-gray-400">
                    {stat.label}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center">
          <div className={`inline-block rounded-xl md:rounded-2xl border ${colors.border} bg-gradient-to-br from-gray-900/80 to-black/80 p-6 md:p-8 max-w-lg mx-auto`}>
            <h4 className="text-lg md:text-xl font-bold text-white mb-3">
              Ready to Transform Your Business?
            </h4>
            <p className="text-sm md:text-base text-gray-300 mb-4 md:mb-6">
              Let's discuss how our {currentService.title.toLowerCase()} services can drive your success
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button className={`${colors.button} text-white px-6 py-3 rounded-lg font-medium hover:opacity-90 transition-opacity`}>
                Start Free Consultation
              </button>
              <button className="px-6 py-3 bg-white/5 border border-gray-700 text-gray-300 rounded-lg font-medium hover:bg-white/10 transition-colors">
                View Case Studies
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Navigation for Mobile */}
      {/* <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 md:hidden z-10">
        <div className="flex items-center gap-1 bg-gray-900/90 backdrop-blur-lg border border-gray-800 rounded-full px-3 py-2">
          {serviceTypes.map((service) => {
            const Icon = service.icon;
            const isActive = activeService === service.id;
            return (
              <button
                key={service.id}
                onClick={() => setActiveService(service.id)}
                className={`p-2 rounded-full transition-all ${
                  isActive
                    ? `${colorVariants[service.color].button} text-white`
                    : 'text-gray-400'
                }`}
              >
                <Icon className="w-4 h-4" />
              </button>
            );
          })}
        </div>
      </div> */}
    </div>
  );
};

export default Slide4Services;